<template>
  <div
    v-if="visible && card"
    class="position-overlay"
    @pointerdown="arm"
    @click.self="onBackdrop"
    @touchstart.stop
    @mousedown.stop
  >
    <div class="position-panel">
      <div class="position-header">
        <h2>{{ headerText }}</h2>
      </div>
      <div class="position-options">
        <button class="position-option" @click="onSelect('ATK')">
          <div class="position-card position-atk">
            <CardArt
              :card-id="card.id"
              :foil="card.foil"
              :alt="localizedName(card.id, card.name)"
              intensity="full"
            />
          </div>
          <div class="position-label atk">{{ t('duel.position.attack') }}</div>
          <div class="position-stats">ATK {{ card.atk ?? '?' }}</div>
        </button>
        <button v-if="mode === 'position'" class="position-option" @click="onSelect('DEF')">
          <div class="position-card position-def">
            <CardArt
              class="position-def-art"
              :card-id="card.id"
              :foil="card.foil"
              :alt="localizedName(card.id, card.name)"
              intensity="full"
            />
          </div>
          <div class="position-label def">{{ t('duel.position.defense') }}</div>
          <div class="position-stats">DEF {{ card.def ?? '?' }}</div>
        </button>
        <button v-else class="position-option" @click="onSelect('SET')">
          <div class="position-card position-def">
            <img :src="sleeveSrc" :alt="t('duel.position.set')" />
          </div>
          <div class="position-label def">{{ t('duel.position.set') }}</div>
          <div class="position-stats">{{ t('duel.position.faceDownDef') }}</div>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from '../i18n/t'
import { localizedName } from '../i18n/localizeCard'
import { currentSleeveUrl } from '../utils/cardImages'
import CardArt from './CardArt.vue'

const { t } = useI18n()

const sleeveSrc = computed(() => currentSleeveUrl())

const props = defineProps<{
  visible: boolean
  card: any | null
  mode?: 'position' | 'summon'
}>()

const emit = defineEmits<{
  select: [position: 'ATK' | 'DEF' | 'SET']
  cancel: []
}>()

/**
 * The tap that chose the monster can land here a moment later, once this
 * sheet has replaced the picker. Ignore that leftover click. A real choice
 * always presses on this sheet first.
 */
const armed = ref(false)

watch(
  () => props.visible,
  () => {
    armed.value = false
  },
)

function arm(): void {
  armed.value = true
}

function onSelect(position: 'ATK' | 'DEF' | 'SET'): void {
  if (!armed.value) return
  armed.value = false
  emit('select', position)
}

function onBackdrop(): void {
  if (!armed.value) return
  armed.value = false
  emit('cancel')
}

const headerText = computed(() => {
  if (!props.card) return ''
  const name = localizedName(props.card.id, props.card.name)
  return props.mode === 'summon'
    ? t('duel.position.summon', { name })
    : t('duel.position.select', { name })
})
</script>

<style scoped>
.position-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: var(--viewer-rail, 400px);
  background: rgba(0, 0, 0, 0.75);
  z-index: 250;
  display: flex;
  align-items: center;
  justify-content: center;
}

.position-panel {
  background: #0d0d1a;
  border: 2px solid #facc15;
  border-radius: 10px;
  padding: 22px 28px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.85);
  min-width: min(460px, calc(100vw - 24px));
}

.position-header {
  text-align: center;
  margin-bottom: 18px;
}

.position-header h2 {
  color: #facc15;
  font-size: 16px;
  margin: 0;
}

.position-options {
  display: flex;
  gap: 60px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
}

.position-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.15s, transform 0.15s;
}

.position-option:hover {
  background: rgba(255, 255, 255, 0.08);
  transform: translateY(-2px);
}

.position-option:active {
  scale: 0.96;
  background: rgba(255, 255, 255, 0.16);
}

.position-card {
  display: flex;
  align-items: center;
  justify-content: center;
}

.position-atk {
  width: 110px;
  height: 160px;
}

.position-atk :deep(.card-art) {
  width: 110px;
  height: 160px;
  border-radius: 4px;
}

.position-def {
  width: 160px;
  height: 110px;
}

.position-def-art {
  width: 110px;
  height: 160px;
  border-radius: 4px;
  transform: rotate(90deg);
}

.position-label {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 1px;
  margin-top: 6px;
}

.position-label.atk {
  color: #ff6666;
}

.position-label.def {
  color: #66aaff;
}

.position-stats {
  color: #ffffff;
  font-size: 12px;
  opacity: 0.85;
}

@media (max-height: 500px) {
  .position-overlay {
    left: 0;
    padding:
      max(8px, env(safe-area-inset-top, 0px))
      max(8px, env(safe-area-inset-right, 0px))
      max(8px, env(safe-area-inset-bottom, 0px))
      max(8px, env(safe-area-inset-left, 0px));
  }

  .position-panel {
    min-width: 0;
    width: min(560px, calc(100vw - 16px));
    max-height: calc(100svh - 16px);
    overflow: auto;
    padding: 12px 16px;
  }

  .position-header {
    margin-bottom: 10px;
  }

  .position-options {
    gap: 16px;
  }

  .position-atk {
    width: 78px;
    height: 112px;
  }

  .position-atk :deep(.card-art) {
    width: 78px;
    height: 112px;
  }

  .position-def {
    width: 112px;
    height: 78px;
  }

  .position-def-art {
    width: 78px;
    height: 112px;
  }
}
</style>
